import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, ChevronRight } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useResource } from '@retink/web-ui'
import { useMutation } from '@retink/web-ui'
import { statusTone } from '@retink/web-ui'
import { drillRow, stopRowActivate } from '@retink/web-ui'
import { Button } from '@retink/web-ui'
import { Badge } from '@retink/web-ui'
import { Input, Field } from '@retink/web-ui'
import { Card, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@retink/web-ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@retink/web-ui'
import { ConfirmDestructive } from '@retink/web-ui'
import { RowActions, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@retink/web-ui'
import { PageHeader } from '@retink/web-ui'
import { EmptyState, TableEmpty } from '@retink/web-ui'
import { LoadingState, ErrorState } from '@retink/web-ui'
import { FormError } from '@retink/web-ui'
import { toast } from '@retink/web-ui'
import { IdentifierPreview, dnsLabelOk } from '../components/IdentifierPreview'

interface Org {
  id: string
  name: string
  slug: string
  billing_email: string | null
  status: string
  tenant_count: number
  created_at: string
  primary_admin: string | null
  sso_status: string | null
  setup_attention: 'no_workspace' | 'connect_microsoft' | 'complete_owner_setup' | 'recover_owner' | 'ready'
}

interface OrganizationSetupResult {
  organization: Org
  workspace: { id: string; name: string }
  ownerSetup: { idpId: string; ownerEmail: string; setupUrl: string; expiresAt: string }
}

const setupLabel: Record<Org['setup_attention'], string> = {
  no_workspace: 'Create workspace',
  connect_microsoft: 'Connect Microsoft',
  complete_owner_setup: 'Owner setup waiting',
  recover_owner: 'Recover administrator',
  ready: 'Ready',
}

// Fleet list of organizations. Click an org to enter its surface (tenants, users,
// IdP). The customer/billing entity — one org → many tenants (BPO) or 1:1.
export function Orgs() {
  const navigate = useNavigate()
  const { data: orgs = [], loading, error, reload } = useResource(() => api.get<Org[]>('/api/admin/orgs'), [])
  const [showCreate, setShowCreate] = useState(false)
  const [search, setSearch] = useState('')
  const [form, setForm] = useState({
    name: '', billingEmail: '', domain: '', ownerEmail: '', workspaceName: '',
  })

  const [edit, setEdit] = useState<Org | null>(null)
  const [editForm, setEditForm] = useState({ name: '', billingEmail: '' })

  // Destructive lifecycle action awaiting type-to-confirm.
  const [lifecycle, setLifecycle] = useState<{ org: Org; action: 'suspend' | 'archive' } | null>(null)

  // No slug field — the server derives it from the name (shared transform).
  const createOrg = useMutation(() => api.post<OrganizationSetupResult>('/api/admin/orgs/setup', {
    organizationName: form.name,
    billingEmail: form.billingEmail || undefined,
    domain: form.domain,
    ownerEmail: form.ownerEmail,
    workspaceName: form.workspaceName,
  }))
  async function create(e: React.FormEvent) {
    e.preventDefault()
    const created = await createOrg.mutate()
    if (!created) return
    setShowCreate(false)
    setForm({ name: '', billingEmail: '', domain: '', ownerEmail: '', workspaceName: '' })
    toast.success('Organization setup created')
    navigate(`/organizations/${created.organization.id}/overview`, { state: { ownerSetup: created.ownerSetup } })
  }

  function openEdit(o: Org) {
    setEdit(o)
    setEditForm({ name: o.name, billingEmail: o.billing_email ?? '' })
    editOrg.reset()
  }

  const editOrg = useMutation(() => api.patch(`/api/admin/orgs/${edit!.id}`, {
    name: editForm.name,
    billingEmail: editForm.billingEmail || null,
  }))
  async function saveEdit(e: React.FormEvent) {
    e.preventDefault()
    if (!edit) return
    if (await editOrg.mutate() === undefined) return
    setEdit(null)
    toast.success('Organization updated')
    reload()
  }

  // Lifecycle = status change. Reactivate is immediate; suspend/archive go
  // through the guarded confirm (they kick everyone in the org out).
  async function setStatus(o: Org, status: 'active' | 'suspended' | 'archived') {
    try {
      await api.patch(`/api/admin/orgs/${o.id}`, { status })
      toast.success(status === 'active' ? `${o.name} reactivated` : `${o.name} ${status}`)
      reload()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update status')
    }
  }

  const needle = search.trim().toLowerCase()
  const visibleOrgs = needle
    ? orgs.filter((org) => [org.name, org.slug, org.primary_admin ?? ''].some((value) => value.toLowerCase().includes(needle)))
    : orgs

  return (
    <div className="p-8">
      <PageHeader
        title="Organizations"
        description="Customers, their Microsoft setup, people, and workspaces."
        action={<Button onClick={() => { createOrg.reset(); setShowCreate(true) }}>+ New organization</Button>}
      />

      {showCreate && (
        <Card className="mb-6 p-5">
          <form onSubmit={create}>
            <h2 className="text-base font-semibold mb-1">New organization</h2>
            <p className="mb-4 text-sm text-muted-foreground">Create customer, first workspace, Microsoft sign-in, and owner setup link together.</p>
            <FormError className="mb-3">{createOrg.error}</FormError>
            <div className="grid gap-3 mb-2 md:grid-cols-2">
              <Field label="Organization name">
                <Input
                  value={form.name}
                  onChange={(e) => setForm({
                    ...form,
                    name: e.target.value,
                    workspaceName: !form.workspaceName || form.workspaceName === form.name
                      ? e.target.value
                      : form.workspaceName,
                  })}
                  required
                  autoFocus
                  placeholder="Acme Corp"
                />
              </Field>
              <Field label="Microsoft domain">
                <Input
                  value={form.domain}
                  onChange={(e) => setForm({ ...form, domain: e.target.value })}
                  required
                  autoCapitalize="none"
                  placeholder="acme.com"
                />
              </Field>
              <Field label="First organization admin">
                <Input type="email" value={form.ownerEmail} required autoCapitalize="none"
                  onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
                  placeholder="admin@acme.com" />
              </Field>
              <Field label="First workspace">
                <Input value={form.workspaceName} required
                  onChange={(e) => setForm({ ...form, workspaceName: e.target.value })}
                  placeholder="Acme Workspace" />
              </Field>
              <Field label="Billing email (optional)">
                <Input type="email" value={form.billingEmail}
                  onChange={(e) => setForm({ ...form, billingEmail: e.target.value })}
                  placeholder="billing@acme.com" />
              </Field>
            </div>
            <IdentifierPreview name={form.name} className="mb-4" />
            <div className="flex gap-2">
              <Button type="submit" disabled={createOrg.saving || !dnsLabelOk(form.name) || !dnsLabelOk(form.workspaceName)}>
                {createOrg.saving ? 'Creating…' : 'Create organization setup'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      <div className="mb-3 max-w-sm">
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search organizations or administrators" aria-label="Search organizations" />
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {['Organization', 'Setup', 'Primary admin', 'Microsoft', 'Workspaces', 'Status', ''].map((h, i) => (
                <TableHead key={h || i}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableEmpty colSpan={7}><LoadingState /></TableEmpty>}
            {!loading && error && <TableEmpty colSpan={7}><ErrorState message={error} onRetry={reload} /></TableEmpty>}
            {!loading && !error && visibleOrgs.length === 0 && (
              <TableEmpty colSpan={7}>
                <EmptyState
                  icon={Building2}
                  title={orgs.length === 0 ? 'No organizations yet' : 'No matching organizations'}
                  description={orgs.length === 0 ? 'Create the first organization to start onboarding workspaces and people.' : 'Try another name or administrator email.'}
                  action={orgs.length === 0 ? <Button onClick={() => { createOrg.reset(); setShowCreate(true) }}>+ New organization</Button> : undefined}
                />
              </TableEmpty>
            )}
            {visibleOrgs.map((o) => (
              <TableRow key={o.id} {...drillRow(() => navigate(`/organizations/${o.id}/overview`))}>
                <TableCell>
                  <div className="font-medium text-foreground transition-colors group-hover:text-primary">{o.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{o.slug}</div>
                </TableCell>
                <TableCell><Badge tone={o.setup_attention === 'ready' ? 'success' : 'warning'}>{setupLabel[o.setup_attention]}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{o.primary_admin ?? 'Not assigned'}</TableCell>
                <TableCell><Badge tone={o.sso_status === 'active' ? 'success' : o.sso_status === 'testing' ? 'warning' : 'neutral'}>{o.sso_status ?? 'not connected'}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{o.tenant_count}</TableCell>
                <TableCell><Badge tone={statusTone(o.status)}>{o.status}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap" {...stopRowActivate}>
                  <div className="flex items-center justify-end gap-1">
                    <ChevronRight className="size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-50" aria-hidden />
                  <RowActions label={`Actions for ${o.name}`}>
                    <DropdownMenuContent>
                      <DropdownMenuItem onSelect={() => openEdit(o)}>Edit</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {o.status !== 'active' && (
                        <DropdownMenuItem onSelect={() => void setStatus(o, 'active')}>Reactivate</DropdownMenuItem>
                      )}
                      {o.status === 'active' && (
                        <DropdownMenuItem destructive onSelect={() => setLifecycle({ org: o, action: 'suspend' })}>Suspend</DropdownMenuItem>
                      )}
                      {o.status !== 'archived' && (
                        <DropdownMenuItem destructive onSelect={() => setLifecycle({ org: o, action: 'archive' })}>Archive</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </RowActions>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Edit / remove org */}
      <Dialog open={edit !== null} onOpenChange={(open) => { if (!open) setEdit(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit organization</DialogTitle>
            <DialogDescription>Status changes (suspend / archive / reactivate) are on the row — they log members out, so they ask you to confirm.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <FormError>{editOrg.error}</FormError>
            <Field label="Name">
              <Input value={editForm.name} required onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </Field>
            <Field label="Billing email">
              <Input type="email" value={editForm.billingEmail} placeholder="billing@acme.com"
                onChange={(e) => setEditForm({ ...editForm, billingEmail: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              <Button type="submit" disabled={editOrg.saving}>{editOrg.saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Suspend / archive — guarded by typing the org name */}
      <ConfirmDestructive
        open={lifecycle !== null}
        onOpenChange={(o) => { if (!o) setLifecycle(null) }}
        title={lifecycle?.action === 'archive' ? `Archive ${lifecycle?.org.name}?` : `Suspend ${lifecycle?.org.name}?`}
        confirmPhrase={lifecycle?.org.name ?? ''}
        confirmLabel={lifecycle?.action === 'archive' ? 'Archive organization' : 'Suspend organization'}
        onConfirm={async () => { if (lifecycle) await setStatus(lifecycle.org, lifecycle.action === 'archive' ? 'archived' : 'suspended') }}
        description={
          lifecycle?.action === 'archive'
            ? 'Retires the org and all its tenants. Everyone under it is signed out immediately and cannot sign back into these tenants. Reactivate later to restore.'
            : 'Suspends the org and all its tenants. Everyone under it is signed out immediately until you reactivate. Platform staff can still enter to investigate.'
        }
      />
    </div>
  )
}
